import type { MetadataFieldDescriptor, MetadataFields, Metadatafieldtypes, MetadataType } from './MetadataFields.js'
import type { OPERATIONS } from './OperationRegistry.ts'
import type { FilterOperationKey, GroupByOperationKey, ProjectionOperationKey, SortByOperationKey } from './OperationRegistry.js'
import type { VisualizationSampleContainer } from './VisualizationSampleContainer.js'

export type ProjectionResult = {
  label: string
  value: string
}

/**
 * The root bucket every pipeline starts from, before any grouping has run.
 *
 * These live here rather than in RunPipeline because GroupOperation needs `defaultKey` to tell a
 * top-level group from a nested one, and importing it from the pipeline runner made a cycle —
 * GroupOperation -> RunPipeline -> OperationRegistry -> GroupOperation. That cycle left `groupByValue`
 * undefined whenever GroupOperation was the module loaded first, which is why it could not be unit
 * tested. OperationTypes imports nothing from the registry, so the cycle cannot re-form.
 */
export const defaultLabel = 'All Samples'
export const defaultKey = 'all'

export type SampleBucket = {
  label: string
  /** stable unique identity of the bucket: React key, URL-persisted selection, and Krona wire group */
  key: string
  samples: VisualizationSampleContainer[]
}

export const NoConfig = {
  defaultConfig: {},
  verifyConfig: (c: unknown) => typeof c === 'object'
}

export interface BaseOperation<T extends string, TConfig> {
  type: 'filter' | 'projection' | 'groupBy' | 'sort'
  key: T
  label: string
  applicableTo: MetadataType[]
  config: {
    defaultConfig: TConfig
    verifyConfig: (c: unknown) => c is TConfig
  }
}

export interface SortOperation<T extends string, TConfig> extends BaseOperation<T, TConfig> {
  type: 'sort'
  apply: (samples: SampleBucket[], field: MetadataFieldDescriptor, config: TConfig) => SampleBucket[]
}

export interface FilterOperation<T extends string, TConfig> extends BaseOperation<T, TConfig> {
  type: 'filter'
  apply: (samples: VisualizationSampleContainer[], field: MetadataFieldDescriptor, config: TConfig) => VisualizationSampleContainer[]
}

export interface ProjectionOperation<T extends string, TConfig> extends BaseOperation<T, TConfig> {
  type: 'projection'
  apply: (sample: VisualizationSampleContainer, field: MetadataFieldDescriptor, config: TConfig) => ProjectionResult
}

export interface GroupByOperation<T extends string, TConfig> extends BaseOperation<T, TConfig> {
  type: 'groupBy'
  /** Names the grouping in prose; the visualisation page joins these into its x-axis label. */
  stringify: (metadataFields: MetadataFields, bound: BoundOperation<string, unknown>) => string
  apply: (
    buckets: SampleBucket[],
    field: MetadataFieldDescriptor,
    config: TConfig,
    projection?: (sample: VisualizationSampleContainer) => ProjectionResult
  ) => SampleBucket[]
}

export interface BoundOperation<T extends string, TConfig> {
  operationKey: T
  metaDataField: Metadatafieldtypes
  config: TConfig
}

export type BoundOperationUnion = {
  [I in keyof typeof OPERATIONS]: (typeof OPERATIONS)[I] extends BaseOperation<infer K, infer C> ? BoundOperation<K, C> : never
}[number]

// Narrowed bound-operation unions per operation category
export type BoundFilterOperation = Extract<BoundOperationUnion, { operationKey: FilterOperationKey }>

export type BoundGroupByOperation = Extract<BoundOperationUnion, { operationKey: GroupByOperationKey }>

export type BoundProjectionOperation = Extract<BoundOperationUnion, { operationKey: ProjectionOperationKey }>

export type BoundSortOperation = Extract<BoundOperationUnion, { operationKey: SortByOperationKey }>

export function isSortByOperation<T extends string, TConfig>(op: BaseOperation<T, TConfig>): op is SortOperation<T, TConfig> {
  return op.type === 'sort'
}

export function isFilterOperation<T extends string, TConfig>(op: BaseOperation<T, TConfig>): op is FilterOperation<T, TConfig> {
  return op.type === 'filter'
}

export function isProjectionOperation<T extends string, TConfig>(op: BaseOperation<T, TConfig>): op is ProjectionOperation<T, TConfig> {
  return op.type === 'projection'
}

export function isGroupByOperation<T extends string, TConfig>(op: BaseOperation<T, TConfig>): op is GroupByOperation<T, TConfig> {
  return op.type === 'groupBy'
}
